import React, { useState, useEffect } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { Translate, TextFormat, getSortState, JhiPagination, JhiItemCount } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { getEntities } from './comex-settings.reducer';
import { IComexSettings } from 'app/shared/model/comex-settings.model';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT, AUTHORITIES } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

export const ComexSettings = (props: RouteComponentProps<{ url: string }>) => {
  const dispatch = useAppDispatch();
  const userAuthorities = useAppSelector(state => state.authentication.account.authorities);

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, ITEMS_PER_PAGE, 'id'), props.location.search)
  );

  const comexSettingsList = useAppSelector(state => state.comexSettings.entities);
  const loading = useAppSelector(state => state.comexSettings.loading);
  const totalItems = useAppSelector(state => state.comexSettings.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      })
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (props.location.search !== endURL) {
      props.history.push(`${props.location.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(props.location.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [props.location.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const { match } = props;

  return (
    <div>
      <h2 id="comex-settings-heading" data-cy="ComexSettingsHeading">
        <Translate contentKey="comexApp.comexSettings.home.title">Comex Settings</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="primary" outline onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="comexApp.comexSettings.home.refreshListLabel">Refresh List</Translate>
          </Button>
          {userAuthorities.includes(AUTHORITIES.ADMIN) && (
            <Link to={`${match.url}/new`} className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
              <FontAwesomeIcon icon="plus" />
              &nbsp;
              <Translate contentKey="comexApp.comexSettings.home.createLabel">Create new Comex Settings</Translate>
            </Link>
          )}
        </div>
      </h2>
      <div className="table-responsive">
        {comexSettingsList && comexSettingsList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="comexApp.comexSettings.id">ID</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('operationStartTime')}>
                  <Translate contentKey="comexApp.comexSettings.operationStartTime">Operation Start Time</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('operationEndTime')}>
                  <Translate contentKey="comexApp.comexSettings.operationEndTime">Operation End Time</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('currencyRateRefresh')}>
                  <Translate contentKey="comexApp.comexSettings.currencyRateRefresh">Currency Rate Refresh</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('serverStatus')}>
                  <Translate contentKey="comexApp.comexSettings.serverStatus">Server Status</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('apiServerStatus')}>
                  <Translate contentKey="comexApp.comexSettings.alshmokhAPIServerStatus">Alshmokh API Server Status</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('feeNormalValue')}>
                  <Translate contentKey="comexApp.comexSettings.feeNormalValue">Fee Normal Value</Translate>
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('feeMediumValue')}>
                  <Translate contentKey="comexApp.comexSettings.feeMediumValue">Fee Medium Value</Translate>
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('notifyCancelUserID')}>
                  <Translate contentKey="comexApp.comexSettings.notifyCancelUserID">Notify Cancel User ID</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {comexSettingsList.map((comexSettings, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`${match.url}/${comexSettings.id}`} color="link" size="sm">
                      {comexSettings.id}
                    </Button>
                  </td>
                  <td>
                    {comexSettings.operationStartTime ? (
                      <TextFormat type="date" value={comexSettings.operationStartTime} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {comexSettings.operationEndTime ? (
                      <TextFormat type="date" value={comexSettings.operationEndTime} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {comexSettings.currencyRateRefresh ? (
                      <TextFormat type="date" value={comexSettings.currencyRateRefresh} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>{comexSettings.serverStatus ? 'true' : 'false'}</td>
                  <td>{comexSettings.apiServerStatus ? 'true' : 'false'}</td>
                  <td>{comexSettings.feeNormalValue}</td>
                  <td>{comexSettings.feeMediumValue}</td>
                  <td>{comexSettings.notifyCancelUserID}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`${match.url}/${comexSettings.id}`} size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      &nbsp;
                      {userAuthorities.includes(AUTHORITIES.ADMIN) && (
                        <Button
                          tag={Link}
                          to={`${match.url}/${comexSettings.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                          color="primary"
                          size="sm"
                          data-cy="entityEditButton"
                        >
                          <FontAwesomeIcon icon="pencil-alt" />{' '}
                          <span className="d-none d-md-inline">
                            <Translate contentKey="entity.action.edit">Edit</Translate>
                          </span>
                        </Button>
                      )}
                      &nbsp;
                      {userAuthorities.includes(AUTHORITIES.ADMIN) && (
                        <Button
                          tag={Link}
                          to={`${match.url}/${comexSettings.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                          color="danger"
                          size="sm"
                          data-cy="entityDeleteButton"
                        >
                          <FontAwesomeIcon icon="trash" />{' '}
                          <span className="d-none d-md-inline">
                            <Translate contentKey="entity.action.delete">Delete</Translate>
                          </span>
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="comexApp.comexSettings.home.notFound">No Comex Settings found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={comexSettingsList && comexSettingsList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default ComexSettings;
